import {
  AthleteActualWorkoutLayer,
  AthleteDraftWorkoutLayer,
  AthleteModifiedPublishedWorkoutLayer,
  AthletePublishedWorkoutLayer,
  BlockDraftWorkoutLayer,
  BlockPreview,
  WorkoutWithCycleAndSession,
} from './types';
import {
  Workout,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutSection,
  WorkoutSuperset,
  EntryFullMutationData,
  ExerciseMetricMutationData,
  SetFullMutationData,
} from '@strength/domain-workout';
import { BasicModel } from '../../../loco-sync/typeUtils';
import { RouterOutput } from '../../../trpc';
import { Result, sortByOrder } from '@strength/utils';
import { WorkoutLayerCommand } from './mutation-fns/types';
import {
  getCycleAndSession,
  getSortedCyclesAndSessions,
} from '../../utils/block-utils';
import { WorkoutStatusById } from './workout-statuses';
import { Metric } from '@strength/domain';

/**
 * Whether the coach has actually published this block to any athlete.
 * Placeholder AthletePublishedWorkoutLayer rows (SC-4258/SC-4299) exist only
 * so AMS-ingested actuals have somewhere to attach on a bowling day the
 * athlete was never prescribed — their mere presence must not count as "the
 * coach published this block," or Block Builder's published checkmark/badge
 * would lie.
 */
export const hasBlockBeenPublished = (
  blockPublishedLayers: { length: number },
  athletePublishedLayers: Pick<AthletePublishedWorkoutLayer, 'recordType'>[],
): boolean =>
  blockPublishedLayers.length > 0 ||
  athletePublishedLayers.some((layer) => !layer.recordType);

export const getDisplayNameForWorkout = (
  workout: WorkoutWithCycleAndSession,
) => {
  return `${workout.session.name} ${workout.cycle.name}`;
};

function loopThroughCyclesAndSessions(
  block: BlockPreview,
  blockWorkoutLayers: BlockDraftWorkoutLayer[],
  athleteWorkoutLayers: AthleteDraftWorkoutLayer[] | undefined,
  handler: (cso: number, sso: number, workout?: WorkoutAndSortOrder) => void,
) {
  const workoutLayout = getWorkoutLayout(
    block,
    blockWorkoutLayers,
    athleteWorkoutLayers,
  );

  for (let cso = 1; cso <= Object.keys(workoutLayout).length; cso++) {
    const c = workoutLayout[cso];
    if (!c) continue;
    const numSessions = Object.keys(c).length;
    for (let sso = 1; sso <= numSessions; sso++) {
      const s = c[sso];
      handler(cso, sso, s);
    }
  }
}

type WorkoutAndSortOrder = { workoutId: string; realSortOrder: number };
type WorkoutLayout = Record<
  number,
  Record<number, WorkoutAndSortOrder | undefined>
>;

/*
  Create a format that matches the block layout like this:
  {
    1: {
      1: { workoutId: 'workout-id-1', realSortOrder: 1 },
      2: undefined,
      3: { workoutId: 'workout-id-2', realSortOrder: 3 }
    },
    2: {
      1: { workoutId: 'workout-id-3', realSortOrder: 1 },
      2: { workoutId: 'workout-id-4', realSortOrder: 2 },
      3: undefined,
      4: undefined,
    }
  }
*/
export function getWorkoutLayout(
  block: BlockPreview,
  blockWorkoutLayers: BlockDraftWorkoutLayer[],
  athleteWorkoutLayers?: AthleteDraftWorkoutLayer[],
) {
  const layout: WorkoutLayout = {};
  const uniqueWorkoutIds = new Set<string>();

  // first fill in placeholder workouts from the block layout
  for (const [cycleSortOrder, sessionCount] of Object.entries(block.layout)) {
    const cso = parseInt(cycleSortOrder, 10);
    for (let sso = 1; sso <= sessionCount; sso++) {
      if (!layout[cso]) {
        layout[cso] = {};
      }
      layout[cso][sso] = undefined;
    }
  }

  // then add all block level workouts
  blockWorkoutLayers.forEach((b) => {
    const { workoutId, cycleSortOrder, sessionSortOrder } = b;
    const cycle = layout[cycleSortOrder];
    if (!cycle) return;
    uniqueWorkoutIds.add(workoutId);
    const athleteLayer = athleteWorkoutLayers?.find(
      (a) => a.workoutId === workoutId,
    );
    if (athleteLayer?.deletedAt) {
      // if the block layer was deleted on the athlete layer, we need to remove it
      delete cycle[sessionSortOrder];
    } else {
      cycle[sessionSortOrder] = { workoutId, realSortOrder: sessionSortOrder };
    }
  });

  // finally fill in athlete layers
  athleteWorkoutLayers
    ?.filter((a) => !a.deletedAt)
    .forEach((a) => {
      const { workoutId, cycleSortOrder, sessionSortOrder } = a;
      // if the id is already in the layout, that means there's a matching block layer,
      // in which case they will be merged later
      if (uniqueWorkoutIds.has(workoutId)) return;

      const cycle = layout[cycleSortOrder];
      if (!cycle) return;
      // we need to insert the athlete layer and bump down the other sessions
      Object.keys(cycle)
        // we intentionally inverse sort so we don't overwrite data
        .sort((a, b) => Number(b) - Number(a))
        .filter((s) => Number(s) >= sessionSortOrder)
        .forEach((s) => {
          const sso = Number(s);
          const existingWorkout = cycle[sso];
          // Update realSortOrder when bumping workout to new position
          cycle[sso + 1] = existingWorkout
            ? { ...existingWorkout, realSortOrder: sso + 1 }
            : existingWorkout;
        });
      cycle[sessionSortOrder] = { workoutId, realSortOrder: sessionSortOrder };
    });

  // normalize the layout session keys so they are sequential
  const normalizedLayout: WorkoutLayout = {};
  Object.keys(layout).forEach((cso) => {
    const cycle = layout[Number(cso)];
    if (!cycle) return;
    const sessions = Object.entries(cycle).sort(
      (a, b) => Number(a[0]) - Number(b[0]),
    );

    const normalizedCycle: Record<number, WorkoutAndSortOrder | undefined> = {};
    for (let sso = 1; sso <= sessions.length; sso++) {
      normalizedCycle[sso] = sessions[sso - 1]?.[1];
    }

    normalizedLayout[Number(cso)] = normalizedCycle;
  });

  return normalizedLayout;
}

// SC-5512: empty grid slots carry NO workout id. Previously their ids were
// derived deterministically from the slot (uuid5 of block/cycle/session) —
// but a live row can drift off its birth slot (sibling bumps, reserve-then-
// edit adds) without changing its immutable id, so the re-derived placeholder
// id collided with that row and every cell of the vacated slot resolved to
// the wrong day document. Empty cells now carry this (cycle, session) slot
// key instead: unique per slot, never a workout id (workout ids are uuids),
// and it encodes the slot so a create built from the cell names its target
// slot explicitly at command-build time — deferred flows (multi-workout
// template apply behind confirmation dialogs) replay commands after
// re-renders and must not re-derive the slot from a fresh id lookup.
const EMPTY_SLOT_CELL_ID_PREFIX = 'empty-slot:';

export function emptySlotCellId(
  cycleSortOrder: number,
  sessionSortOrder: number,
): string {
  return `${EMPTY_SLOT_CELL_ID_PREFIX}${cycleSortOrder}:${sessionSortOrder}`;
}

export function parseEmptySlotCellId(
  workoutId: string,
): { cycleSortOrder: number; sessionSortOrder: number } | null {
  if (!workoutId.startsWith(EMPTY_SLOT_CELL_ID_PREFIX)) return null;
  const rest = workoutId.slice(EMPTY_SLOT_CELL_ID_PREFIX.length);
  const [cycleSortOrder, sessionSortOrder] = rest.split(':').map(Number);
  if (!cycleSortOrder || !sessionSortOrder) return null;
  return { cycleSortOrder, sessionSortOrder };
}

export function mergeWorkoutsWithCyclesAndSessions(
  block: BlockPreview,
  blockWorkoutLayers: BlockDraftWorkoutLayer[],
  workouts: Workout[],
  actualWorkoutLayers: AthleteActualWorkoutLayer[],
  modifiedPublishedWorkoutLayers: AthleteModifiedPublishedWorkoutLayer[],
  workoutStatuses: WorkoutStatusById[],
  athleteWorkoutLayers?: AthleteDraftWorkoutLayer[],
): WorkoutWithCycleAndSession[] {
  const result: WorkoutWithCycleAndSession[] = [];
  loopThroughCyclesAndSessions(
    block,
    blockWorkoutLayers,
    athleteWorkoutLayers,
    (cso, sso, workout) => {
      // This is the real sort order of the workout, whereas sso is the adjusted
      // sort order given that a block layer and athlete layer share the same one
      // we need to preserve this value so actions taken on the workout reference
      // the correct sort order
      const realSortOrder = workout?.realSortOrder;
      const workoutId = workout?.workoutId;

      const { cycle, session } = getCycleAndSession(
        block,
        cso,
        realSortOrder ?? sso,
        sso,
      );

      const workoutMatch = workouts.find((w) => w.workoutId === workoutId);

      const workoutStatus = workoutStatuses.find(
        (ws) => ws.workoutId === workoutMatch?.workoutId,
      )?.status;

      const actualLayersForWorkout = actualWorkoutLayers.filter(
        (aw) => aw.workoutId === workoutId,
      );

      const modifiedPublishedLayersForWorkout =
        modifiedPublishedWorkoutLayers.filter(
          (mp) => mp.workoutId === workoutId,
        );

      if (workoutMatch) {
        result.push({
          ...workoutMatch,
          actual: actualLayersForWorkout,
          modifiedPublished: modifiedPublishedLayersForWorkout,
          exists: true,
          assignedDate: workoutMatch?.assignedDate ?? null,
          status: workoutStatus ?? { type: 'planned' },
          session,
          cycle,
          isAthleteOnlyLayer: !!athleteWorkoutLayers?.some(
            (a) => a.workoutId === workoutId,
          ),
        });
      } else {
        // Empty slot — a (cycle, session) key, not a workout id (SC-5512)
        result.push({
          workoutId: emptySlotCellId(cycle.id, session.id),
          sections: [],
          exists: false,
          actual: undefined,
          modifiedPublished: undefined,
          assignedDate: null,
          status: { type: 'draft' },
          session,
          cycle,
          isAthleteOnlyLayer: false,
          name: null,
        });
      }
    },
  );

  return result;
}

export function isLastExerciseInGroup(
  alternateGroup: WorkoutAlternateGroup | undefined | null,
  workoutExerciseId: string,
) {
  if (!alternateGroup?.exercises || alternateGroup.exercises.length === 0)
    return false;

  return (
    sortByOrder(alternateGroup.exercises).findIndex(
      (e) => e.exercise.workoutExerciseId === workoutExerciseId,
    ) ===
    alternateGroup.exercises.length - 1
  );
}

export type WorkoutIdWithCycleAndSession = {
  workoutId: string;
  assignedDate: string | null;
  session: BlockPreview['sortedSessions'][0];
  cycle: BlockPreview['sortedCycles'][0];
};

export function mergeWorkoutIdsWithCyclesAndSessions(
  block: BlockPreview,
  blockWorkoutLayers: BasicModel<'BlockDraftWorkoutLayer'>[],
): WorkoutIdWithCycleAndSession[] {
  const result: WorkoutIdWithCycleAndSession[] = [];
  loopThroughCyclesAndSessions(
    block,
    blockWorkoutLayers,
    undefined,
    (cso, sso) => {
      const match = blockWorkoutLayers.find(
        (w) => w.cycleSortOrder === cso && w.sessionSortOrder === sso,
      );
      const { cycle, session } = getCycleAndSession(block, cso, sso);
      if (match) {
        result.push({
          workoutId: match.workoutId,
          assignedDate: match.assignedDate ?? null,
          session,
          cycle,
        });
      }
    },
  );

  return result;
}

export type BlockForDraft = RouterOutput['block']['blockForDraft']['block'];

export function blockDraftToPreview(block: BlockForDraft): BlockPreview {
  return {
    ...block,
    focuses: block.focuses.map((blockFocus) => {
      return {
        ...blockFocus,
        name: blockFocus.focus.name,
      };
    }),
    ...getSortedCyclesAndSessions(block),
  };
}

export function exercisesForSection(section: WorkoutSection): {
  exercise: WorkoutExercise;
  superset: WorkoutSuperset | null;
  alternateGroup: WorkoutAlternateGroup | null;
}[] {
  const result: {
    exercise: WorkoutExercise;
    superset: WorkoutSuperset | null;
    alternateGroup: WorkoutAlternateGroup | null;
  }[] = [];

  const supersets = new Map<string, WorkoutSuperset>();
  const altGroups = new Map<string, WorkoutAlternateGroup>();

  for (const entry of section.entries) {
    if (entry.__type === 'WorkoutSuperset') {
      for (const { exercise } of entry.exercises) {
        supersets.set(exercise.workoutExerciseId, entry);
      }
    } else if (entry.__type === 'WorkoutAlternateGroup') {
      for (const { exercise } of entry.exercises) {
        altGroups.set(exercise.workoutExerciseId, entry);
      }
    }
  }

  const processed = new Set<string>();
  const processExercise = (exercise: WorkoutExercise) => {
    const { workoutExerciseId } = exercise;
    if (processed.has(workoutExerciseId)) return;

    const alternateGroup = altGroups.get(workoutExerciseId) ?? null;
    const altExercise = alternateGroup?.exercises.find(
      (e) => e.workoutExerciseId === workoutExerciseId,
    );
    const isAlt = alternateGroup && altExercise?.sortOrder !== 0;
    const superset = isAlt ? null : (supersets.get(workoutExerciseId) ?? null);

    result.push({ exercise, superset, alternateGroup });
    processed.add(workoutExerciseId);

    // This is a base exercise; handle all the alternates now so they stay grouped together
    if (alternateGroup && !isAlt) {
      for (const { exercise: alt } of sortByOrder(alternateGroup.exercises)) {
        if (alt.sortOrder === 0) continue;
        processExercise(alt);
      }
    }
  };

  for (const entry of section.entries) {
    if (entry.__type === 'WorkoutExercise') {
      // Single exercise
      processExercise(entry);
    } else {
      for (const { exercise } of entry.exercises) {
        processExercise(exercise);
      }
    }
  }

  return result;
}

export function buildResponse(
  commands: WorkoutLayerCommand[] | null,
  error?: string,
): Result<WorkoutLayerCommand[], string> {
  if (commands) {
    return {
      ok: true,
      value: commands,
    };
  } else {
    return {
      ok: false,
      error: error ? error : 'Could not complete operation',
    };
  }
}

export function ensureSetsMetricInExercise(
  metrics: ExerciseMetricMutationData[],
  setsMetric: Metric,
  baseLayerMetrics?: ExerciseMetricMutationData[],
): ExerciseMetricMutationData[] {
  // Combine incoming metrics with base layer metrics to check if Sets exists
  const allMetrics = [...metrics, ...(baseLayerMetrics ?? [])];
  const hasSetsMetric = allMetrics.some((m) => m.metricType === 'sets');

  // Add Sets metric if it doesn't exist
  const normalizedMetrics = hasSetsMetric
    ? metrics
    : [
        {
          metricId: setsMetric.id,
          metricName: setsMetric.name,
          metricType: setsMetric.type,
          sortOrder: setsMetric.sortOrder,
        },
        ...metrics,
      ];

  // Return metrics (potentially with Sets added if it was missing)
  return normalizedMetrics;
}

export function ensureDefaultSetWithSetsMetric(
  sets: SetFullMutationData[],
  setsMetric: Metric,
): SetFullMutationData[] {
  return sets.map((set) =>
    set.metrics.some((m) => m.metricId === setsMetric.id)
      ? set
      : {
          ...set,
          metrics: [...set.metrics, { metricId: setsMetric.id, input: '1' }],
        },
  );
}

// When copying and pasting multiples entries, we need to ensure that the sets metric is present and that the sets have a default set.
// use case: copying and pasting entries from a Draft that has been completed (no Sets metric shown)
export function normalizeEntriesForDraft(
  entries: EntryFullMutationData[],
  setsMetric: Metric,
): EntryFullMutationData[] {
  return entries.map((entry) => {
    if (entry.__type === 'WorkoutExercise') {
      return {
        ...entry,
        metrics: ensureSetsMetricInExercise(entry.metrics, setsMetric),
        sets: ensureDefaultSetWithSetsMetric(entry.sets, setsMetric),
      };
    } else if (entry.__type === 'WorkoutAlternateGroup') {
      return {
        ...entry,
        exercises: entry.exercises.map((altEx) => ({
          ...altEx,
          exercise: {
            ...altEx.exercise,
            metrics: ensureSetsMetricInExercise(
              altEx.exercise.metrics,
              setsMetric,
            ),
            sets: ensureDefaultSetWithSetsMetric(
              altEx.exercise.sets,
              setsMetric,
            ),
          },
        })),
      };
    } else if (entry.__type === 'WorkoutSuperset') {
      return {
        ...entry,
        exercises: entry.exercises.map((supersetEx) => ({
          ...supersetEx,
          exercise: {
            ...supersetEx.exercise,
            metrics: ensureSetsMetricInExercise(
              supersetEx.exercise.metrics,
              setsMetric,
            ),
            sets: ensureDefaultSetWithSetsMetric(
              supersetEx.exercise.sets,
              setsMetric,
            ),
          },
        })),
      };
    }
    return entry;
  });
}
